/**
 * Microdose Optimization dose steps on the clinic patient page, Plan tab (workbook MCOS_Microdose_Optimization_
 * Clinical_Intelligence v1.2, tab 10A).
 *
 *   Before signing (OPT-STEP-001): the ladder the plan holds, the minimum 4 weeks, "Include ceiling step" (off by
 *   default) and "Review and sign this plan", which opens the sign drawer on this plan, the one that holds the ladder.
 *   It is signed with the plan; after that it cannot change (OPT-STEP-008).
 *   After the patient accepts (OPT-STEP-004): the one-tap card. Current dose and weeks at it, the dispensed vial,
 *   check-in scores against baseline, side effects, each readiness check with pass or the reason, the new dose in mg,
 *   mL and units at the dispensed concentration, and Approve step / Stay / Step down for a prescriber licensed in the
 *   patient's state.
 *
 * Clinicians see checks and labels, never a score or a chance of success; rule IDs sit on hover, never in the layout.
 * Every rule runs on the server (services/microdose.service.ts); this card only shows what the API says is allowed, so
 * the two cannot disagree. The card renders nothing for a patient without a microdose plan.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatDate } from '../lib/format';
import { Button, ErrorState, InfoHint, inputClass } from './ui';
import { IconAlert, IconCheck, IconChevronDown, IconSyringe } from './icons';
import { MicrodoseDecisionDialog, type DecisionKind } from './MicrodoseDecisionDialog';
import { SignDrawer } from './SignDrawer';
import { blockerTitle, doseLine, plain, plainReason, unitCount, unitsAt, type CardData, type DraftLadder, type SignedLadder, type StepView, type Viewer } from './MicrodoseTypes';

/* ------------------------------------------------------------------ *
 * Small pieces
 * ------------------------------------------------------------------ */

function Section({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <section data-testid={testId} className="mb-3 rounded-[10px] border border-hairline bg-white px-4 py-4">
      {children}
    </section>
  );
}

function Eyebrow({ children }: { children: ReactNode }) {
  return <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-muted">{children}</p>;
}

type Tone = 'forest' | 'yellow' | 'orange' | 'red' | 'info' | 'muted';
const TONE: Record<Tone, string> = {
  forest: 'bg-mint text-forest',
  red: 'bg-alert-soft text-alert',
  yellow: 'bg-risk-yellow-soft text-risk-yellow',
  orange: 'bg-risk-orange-soft text-risk-orange',
  info: 'bg-info-soft text-navy',
  muted: 'bg-paper-deep text-ink-muted',
};

/** A status, never a control: a dot, no border, no hover. */
function StatusBadge({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-[5px] text-[12.5px] font-semibold leading-none ${TONE[tone]}`}>
      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {children}
    </span>
  );
}

const LEVEL_CLASS: Record<'RED' | 'ORANGE' | 'YELLOW', string> = {
  RED: 'bg-alert-soft text-alert',
  ORANGE: 'bg-risk-orange-soft text-risk-orange',
  YELLOW: 'bg-risk-yellow-soft text-risk-yellow',
};

function LadderStrip({
  steps,
  ceiling,
  includeCeiling,
  currentStep,
  nextStep,
  pendingStep,
}: {
  steps: StepView[];
  ceiling: StepView;
  includeCeiling: boolean;
  currentStep?: number | null;
  nextStep?: number | null;
  pendingStep?: number | null;
}) {
  const all = [...steps.slice(0, 3), ceiling];
  return (
    <ol className="grid grid-cols-4 gap-1.5" aria-label="The signed ladder">
      {all.map((s) => {
        const unsigned = s.step === 4 && !includeCeiling;
        const current = currentStep === s.step;
        const state = current
          ? 'Current'
          : pendingStep === s.step
            ? 'Waiting'
            : nextStep === s.step
              ? 'Next'
              : unsigned
                ? 'Not signed'
                : null;
        return (
          <li
            key={s.step}
            aria-current={current ? 'step' : undefined}
            className={`min-w-0 rounded-lg border px-1.5 py-2 text-center ${
              current ? 'border-forest bg-mint' : unsigned ? 'border-dashed border-hairline bg-paper' : 'border-hairline bg-white'
            }`}
          >
            <p className={`truncate text-[10.5px] font-semibold uppercase tracking-[0.06em] ${current ? 'text-forest' : 'text-ink-muted'}`}>{s.label}</p>
            <p className={`mt-0.5 font-display text-[17px] leading-tight sm:text-[19px] ${unsigned ? 'text-ink-muted' : 'text-navy'}`} style={{ fontWeight: 560 }}>
              {s.text}
              <span className="ml-0.5 font-sans text-[11.5px] font-normal text-ink-muted">mg</span>
            </p>
            <p className={`mt-0.5 h-[15px] truncate text-[11px] ${current ? 'font-semibold text-forest' : 'text-ink-muted'}`}>{state ?? ''}</p>
          </li>
        );
      })}
    </ol>
  );
}

function Tile({ label, children, aside }: { label: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="min-w-0 rounded-lg border border-hairline px-3.5 py-3">
      <div className="flex items-center justify-between gap-2">
        <Eyebrow>{label}</Eyebrow>
        {aside}
      </div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Big({ children }: { children: ReactNode }) {
  return (
    <p className="font-display text-[22px] leading-tight text-navy" style={{ fontWeight: 560 }}>
      {children}
    </p>
  );
}

function Fine({ children, tone = 'muted' }: { children: ReactNode; tone?: 'muted' | 'warn' | 'ok' }) {
  const cls = tone === 'warn' ? 'text-risk-orange' : tone === 'ok' ? 'text-forest' : 'text-ink-muted';
  return <p className={`mt-0.5 text-[12.5px] leading-snug ${cls}`}>{children}</p>;
}

/** The card's own loading state: shown only when the lookup takes a moment, so other patients' Plan tabs never flash. */
function CardSkeleton() {
  return (
    <Section testId="microdose-loading">
      <div className="space-y-2.5" aria-busy="true" aria-label="Loading the microdose dose card">
        <div className="h-5 w-56 animate-pulse rounded bg-paper-deep" />
        <div className="grid grid-cols-4 gap-1.5">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-[66px] animate-pulse rounded-lg bg-paper-deep" />
          ))}
        </div>
        <div className="h-16 animate-pulse rounded-lg bg-paper-deep" />
      </div>
    </Section>
  );
}

/* ------------------------------------------------------------------ *
 * The card
 * ------------------------------------------------------------------ */

export function MicrodoseDoseCard({ patientId }: { patientId: string }) {
  const query = useQuery({
    queryKey: ['microdose', patientId],
    queryFn: () => api.get<CardData>(`/microdose/patients/${patientId}`),
  });
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!query.isLoading) return;
    const timer = window.setTimeout(() => setSlow(true), 200);
    return () => window.clearTimeout(timer);
  }, [query.isLoading]);

  if (query.isLoading) return slow ? <CardSkeleton /> : null;
  if (query.error) {
    return (
      <Section>
        <p className="mb-2 font-display text-[18px] text-navy">Microdose dose steps</p>
        <ErrorState error={query.error} retry={() => void query.refetch()} />
      </Section>
    );
  }
  const data = query.data;
  if (!data || (!data.draft && !data.ladder)) return null;

  return (
    <>
      {data.draft && <DraftLadderCard draft={data.draft} viewer={data.viewer} patientId={patientId} replacing={Boolean(data.ladder)} firstName={data.patient.firstName} />}
      {data.ladder && <SignedLadderCard ladder={data.ladder} viewer={data.viewer} patientId={patientId} firstName={data.patient.firstName} />}
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Before signing: the ladder, and signing the plan that holds it (OPT-STEP-001)
 * ------------------------------------------------------------------ */

function DraftLadderCard({ draft, viewer, patientId, replacing, firstName }: { draft: DraftLadder; viewer: Viewer; patientId: string; replacing: boolean; firstName: string }) {
  const client = useQueryClient();
  const [, setParams] = useSearchParams();
  const [signing, setSigning] = useState(false);
  const [outcome, setOutcome] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(null);
  const save = useMutation({
    mutationFn: (includeCeiling: boolean) => api.put(`/microdose/plans/${draft.planId}/ladder`, { includeCeiling }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['microdose', patientId] }),
  });
  const prepare = useMutation({
    mutationFn: () => api.post<{ ready: boolean; unpriced: string[] }>(`/microdose/plans/${draft.planId}/prepare-sign`, {}),
    onSuccess: (result) => {
      if (result.ready) setSigning(true);
    },
  });
  const ceilingOn = save.isPending ? Boolean(save.variables) : draft.includeCeiling;
  const canChange = viewer.canEdit && !draft.lockedReason;
  const unpriced = prepare.data && !prepare.data.ready ? prepare.data.unpriced : [];
  const openOpr = () =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', 'opr');
        return next;
      },
      { replace: true },
    );

  return (
    <Section testId="microdose-ladder-draft">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <IconSyringe size={19} className="text-forest" />
            <h3 className="font-display text-[20px] text-navy">Microdose dose ladder</h3>
            <InfoHint label="About the dose ladder">
              The signed plan holds the whole ladder: the start dose, each allowed step, the HOLD dose and the minimum
              4 weeks at each step. Steps inside it need only your one-tap approval and the patient's confirmation, never a
              new plan. Signing the plan signs this ladder; after that it cannot change (OPT-STEP-001, OPT-STEP-008).
            </InfoHint>
          </div>
          <p className="mt-0.5 text-[13.5px] text-ink-muted">
            {draft.itemName} · plan v{draft.planVersion}
            {replacing ? ' · replaces the signed ladder once accepted' : ''}
          </p>
        </div>
        <StatusBadge tone="muted">{draft.planStatus === 'awaiting_signature' ? 'Waiting for signature' : 'Draft, not signed'}</StatusBadge>
      </div>

      <div className="mt-3">
        <LadderStrip steps={draft.steps} ceiling={draft.ceiling} includeCeiling={ceilingOn} currentStep={null} nextStep={null} />
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-[1fr_1fr] lg:items-start">
        <div className="rounded-lg border border-hairline px-3.5 py-3">
          <label className={`flex items-start gap-3 ${canChange ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}>
            <input
              type="checkbox"
              className="mt-0.5 h-[18px] w-[18px] shrink-0 accent-[var(--forest)]"
              checked={ceilingOn}
              disabled={!canChange || save.isPending}
              onChange={(e) => save.mutate(e.target.checked)}
              data-testid="include-ceiling"
            />
            <span className="min-w-0">
              <span className="flex items-center gap-1.5 text-[14px] font-semibold text-navy">
                Include ceiling step ({draft.ceiling.text} mg)
                <InfoHint label="About the ceiling step">
                  Off by default. With it signed, you may move a patient from HOLD to {draft.ceiling.text} mg when you document
                  a need for more effect, with a short reason. Without it, going above HOLD needs a new plan version
                  (OPT-HE-008, OPT-HE-010, OPT-STEP-008).
                </InfoHint>
              </span>
              <span className="mt-0.5 block text-[12.5px] text-ink-muted">
                {!viewer.canEdit
                  ? 'A clinician sets the ladder'
                  : draft.lockedReason
                    ? draft.lockedReason
                    : save.isPending
                      ? 'Saving'
                      : draft.saved
                        ? 'Saved with the draft'
                        : 'Default ladder, signed as shown'}
              </span>
            </span>
          </label>
          <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 border-t border-hairline pt-2.5 text-[13px]">
            <dt className="text-ink-muted">Start dose</dt>
            <dd className="text-navy">{draft.start.text} mg once a week</dd>
            <dt className="text-ink-muted">Minimum at each step</dt>
            <dd className="text-navy">{draft.minWeeksAtStep} weeks</dd>
            <dt className="text-ink-muted">HOLD</dt>
            <dd className="text-navy">{draft.steps[2]?.text} mg</dd>
            <dt className="text-ink-muted">Ceiling</dt>
            <dd className="text-navy">{ceilingOn ? `${draft.ceiling.text} mg, signed with the plan` : 'Not signed'}</dd>
          </dl>
          {save.error && <p className="mt-2 text-[12.5px] text-alert" role="alert">{save.error instanceof Error ? save.error.message : 'Could not save the ladder'}</p>}
        </div>
        <div className="rounded-lg bg-paper-deep px-3.5 py-3">
          <div className="flex items-center gap-1.5">
            <Eyebrow>What the patient reads on the signed plan</Eyebrow>
            <InfoHint label="Before the plan is signed">
              Until the plan is signed, {firstName} sees only that their clinician is reviewing their results: no drug, dose
              or score (WHY-HOLD-01, OPT-TRIG-013).
            </InfoHint>
          </div>
          <p className="mt-1.5 font-display text-[15.5px] leading-snug text-navy" data-testid="ladder-sentence">
            {draft.patientSentence}
          </p>
        </div>
      </div>

      {/* Signing the plan that holds this ladder (OPT-STEP-001: the prescriber signs once). */}
      {viewer.canEdit && (
        <div className="mt-3 flex flex-col gap-2.5 border-t border-hairline pt-3 sm:flex-row sm:items-center sm:justify-between" data-testid="microdose-sign-row">
          <p className="flex items-center gap-1.5 text-[13px] text-ink-muted">
            {draft.lockedReason ?? `Signing plan v${draft.planVersion} signs this ladder as shown above.`}
            <InfoHint label="About signing">
              Opens Review and sign on this plan, the one that holds {draft.itemName} and this ladder. Every sign-off check
              runs again on the server. A plan built from a nurse account goes to the physician's Sign queue instead.
            </InfoHint>
          </p>
          {!draft.lockedReason && (
            <Button onClick={() => { setOutcome(null); prepare.mutate(); }} loading={prepare.isPending} className="w-full sm:w-auto">
              Review and sign this plan
            </Button>
          )}
        </div>
      )}
      {unpriced.length > 0 && (
        <div className="mt-2.5 flex flex-col gap-2 rounded-lg bg-risk-yellow-soft px-3.5 py-2.5 text-[13px] text-navy sm:flex-row sm:items-center sm:justify-between" role="status" data-testid="microdose-unpriced">
          <span>Set the monthly price for {unpriced.join(', ')} before this plan can go.</span>
          <button type="button" onClick={openOpr} className="shrink-0 text-left font-semibold text-forest hover:underline">
            Open OPR Pricing
          </button>
        </div>
      )}
      {prepare.error && <p className="mt-2 text-[12.5px] text-alert" role="alert">{prepare.error instanceof Error ? prepare.error.message : 'Could not open Review and sign'}</p>}
      {outcome && (
        <p role="status" className={`mt-2.5 rounded-md px-3 py-2 text-[13px] ${outcome.tone === 'ok' ? 'bg-mint text-forest' : 'bg-risk-yellow-soft text-risk-yellow'}`}>
          {outcome.text}
        </p>
      )}

      {signing && (
        <SignDrawer
          planId={draft.planId}
          patientId={patientId}
          onClose={() => {
            setSigning(false);
            void client.invalidateQueries({ queryKey: ['microdose', patientId] });
          }}
          onSent={(text, tone) => {
            setOutcome({ tone, text });
            void client.invalidateQueries({ queryKey: ['microdose', patientId] });
            void client.invalidateQueries({ queryKey: ['workspace', patientId] });
          }}
        />
      )}
    </Section>
  );
}

/* ------------------------------------------------------------------ *
 * After signing: the one-tap card (OPT-STEP-004)
 * ------------------------------------------------------------------ */

function statusOf(l: SignedLadder, firstName: string): { tone: Tone; text: string } {
  // OPT-HE-004: a RED check-in item is "hold medication pathway and urgent clinician assessment", whatever else waits.
  if (l.alerts.some((a) => a.level === 'RED')) return { tone: 'red', text: 'Urgent clinician assessment' };
  switch (l.options.status) {
    case 'ready':
      return { tone: 'forest', text: 'Next step ready for review' };
    case 'ceiling_available':
      return { tone: 'forest', text: 'Ceiling step available' };
    case 'pending':
      return l.pending?.overdue ? { tone: 'orange', text: 'Not confirmed in 3 days' } : { tone: 'info', text: `Waiting for ${firstName} to confirm` };
    case 'no_checkin':
      return l.vial ? vialStatus(l, firstName) : { tone: 'muted', text: 'Waiting for a check-in' };
    case 'not_active':
      return { tone: 'muted', text: `Waiting for ${firstName} to accept the plan` };
    default:
      return l.vial ? vialStatus(l, firstName) : { tone: 'yellow', text: 'Stay at current dose' };
  }
}

function vialStatus(l: SignedLadder, firstName: string): { tone: Tone; text: string } {
  return l.vial?.overdue ? { tone: 'orange', text: 'New vial not confirmed in 3 days' } : { tone: 'info', text: `Waiting for ${firstName} to confirm the new vial` };
}

function SignedLadderCard({ ladder: l, viewer, patientId, firstName }: { ladder: SignedLadder; viewer: Viewer; patientId: string; firstName: string }) {
  const [dialog, setDialog] = useState<DecisionKind | null>(null);
  const [vialOpen, setVialOpen] = useState(false);
  const status = statusOf(l, firstName);
  const next = l.options.approve;
  const down = l.options.stepDown;
  const pendingStep = l.pending ? l.steps.find((s) => s.text === l.pending!.toText)?.step ?? (l.pending.toText === l.ceiling.text ? 4 : null) : null;
  const unmeasurable = next.blockers.find((b) => b.rule === 'OPT-STEP-006' && /cannot measure/.test(b.text));
  /** OPT-STEP-008, OPT-TC-012: a step above the signed ladder needs a new plan version; said on the card, not only on hover. */
  const newVersion = next.blockers.find((b) => b.rule === 'OPT-STEP-008') ?? null;

  return (
    <Section testId="microdose-dose-card">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <IconSyringe size={19} className="text-forest" />
            <h3 className="font-display text-[20px] text-navy">Microdose dose steps</h3>
            <InfoHint label="About dose steps">
              Time makes the next step available for review; it never changes the dose by itself. A step needs every
              readiness check to pass, a prescriber's approval with their signing PIN or passkey, and the patient's
              confirmation. Only then does the new dose reach their schedule (OPT-STEP-003 to OPT-STEP-009).
            </InfoHint>
          </div>
          <p className="mt-0.5 text-[13.5px] text-ink-muted">
            {l.itemName} · plan v{l.planVersion} signed {formatDate(l.signedAt)}
            {l.signedBy ? ` by ${l.signedBy}` : ''}
            {l.acceptedAt ? ` · accepted ${formatDate(l.acceptedAt)}` : ''}
          </p>
        </div>
        <StatusBadge tone={status.tone}>{status.text}</StatusBadge>
      </div>

      {/* The signed ladder */}
      <div className="mt-3">
        <LadderStrip
          steps={l.steps}
          ceiling={l.ceiling}
          includeCeiling={l.includeCeiling}
          currentStep={l.active ? l.current.step : null}
          nextStep={l.active && next.allowed ? next.toStep : null}
          pendingStep={pendingStep}
        />
        <p className="mt-1.5 flex flex-wrap items-center gap-x-1.5 text-[12.5px] text-ink-muted">
          Minimum {l.minWeeksAtStep} weeks at each step · ceiling {l.includeCeiling ? 'signed' : 'not signed'}
          <InfoHint label="About the signed ladder">
            Signed with the plan. A different ladder, a different drug, going above it, or restarting after 4 or more weeks
            off needs a new plan version (OPT-STEP-008). The patient read: "{l.patientSentence}"
          </InfoHint>
        </p>
      </div>

      {/* Current, next, vial */}
      <div className="mt-3 grid gap-2.5 sm:grid-cols-3">
        <Tile label="Current dose">
          <Big>{l.current.text} mg weekly</Big>
          {l.active ? (
            new Date(l.current.since).getTime() > Date.now() ? (
              <Fine>On the schedule from {formatDate(l.current.since)}, the next weekly dose day</Fine>
            ) : (
              <Fine>
                {l.current.weeks} {l.current.weeks === 1 ? 'week' : 'weeks'} at this dose · since {formatDate(l.current.since)}
              </Fine>
            )
          ) : (
            <Fine>Starts once {firstName} accepts the plan</Fine>
          )}
          {l.current.dose ? <Fine>{doseLine(l.current.dose)}</Fine> : <Fine tone="warn">No vial recorded yet</Fine>}
        </Tile>
        <Tile label={l.pending ? 'Waiting to be confirmed' : next.ceiling ? 'Ceiling step' : 'Next step'}>
          {l.pending ? (
            <>
              <Big>{l.pending.toText} mg weekly</Big>
              {l.pending.dose && <Fine tone={l.pending.dose.measurable ? 'muted' : 'warn'}>{doseLine(l.pending.dose)}</Fine>}
              <Fine>Not on {firstName}'s schedule until confirmed</Fine>
            </>
          ) : next.toText ? (
            <>
              <Big>{next.toText} mg weekly</Big>
              {next.dose ? (
                next.dose.measurable ? (
                  <Fine>{doseLine(next.dose)}</Fine>
                ) : (
                  <Fine tone="warn">
                    {unitCount(next.dose.units)} at {plain(next.dose.concentrationMgPerMl)} mg/mL cannot be measured on a U-100 syringe. Choose another concentration.
                  </Fine>
                )
              ) : (
                <Fine>Units appear once a vial is recorded</Fine>
              )}
              {next.allowed ? (
                <Fine tone="ok">Ready for review</Fine>
              ) : newVersion ? (
                <span title={newVersion.rule ?? undefined}>
                  <Fine tone="warn">{newVersion.text}</Fine>
                </span>
              ) : l.readiness && !l.readiness.ready ? (
                <Fine>Not ready: see the checks</Fine>
              ) : null}
            </>
          ) : (
            <>
              <Big>None</Big>
              <span title={blockerTitle(next.blockers)}>
                <Fine>{next.blockers[0]?.text ?? 'No higher step in the signed ladder'}</Fine>
              </span>
            </>
          )}
        </Tile>
        <Tile
          label="Dispensed vial"
          aside={
            viewer.canEdit && !vialOpen ? (
              <button type="button" onClick={() => setVialOpen(true)} className="text-[12.5px] font-semibold text-forest hover:underline" data-testid="record-vial">
                {l.dispense ? 'New vial' : 'Record vial'}
              </button>
            ) : null
          }
        >
          {l.dispense ? (
            <>
              <Big>{plain(l.dispense.concentrationMgPerMl)} mg/mL</Big>
              <Fine>Filled {formatDate(l.dispense.dispensedAt)}{l.dispense.recordedBy ? ` · recorded by ${l.dispense.recordedBy}` : ''}</Fine>
            </>
          ) : (
            <>
              <Big>Not recorded</Big>
              <Fine>Units and mL always come from the concentration on the vial.</Fine>
            </>
          )}
        </Tile>
      </div>

      {vialOpen && <VialForm ladder={l} patientId={patientId} firstName={firstName} onClose={() => setVialOpen(false)} />}

      {/* A change waiting for the patient */}
      {l.pending && (
        <div className={`mt-3 flex gap-3 rounded-lg px-3.5 py-3 ${l.pending.overdue || (l.pending.dose && !l.pending.dose.measurable) ? 'bg-risk-orange-soft' : 'bg-info-soft'}`} data-testid="pending-change">
          <span className={`mt-0.5 shrink-0 ${l.pending.overdue ? 'text-risk-orange' : 'text-navy'}`}>
            <IconAlert size={18} />
          </span>
          <div className="min-w-0 text-[13.5px] text-navy">
            <p className="font-semibold">
              {l.pending.kind === 'step_down' ? 'Step down' : 'Step'} to {l.pending.toText} mg waiting for {firstName} to confirm
            </p>
            <p className="mt-0.5 text-ink-muted">
              {l.pending.by ? `${l.pending.by} decided ${formatDate(l.pending.since)}. ` : ''}
              {l.pending.dose ? `${firstName} sees ${doseLine(l.pending.dose)}. ` : ''}
              The dose stays at {l.current.text} mg until {firstName} confirms.
              {l.pending.overdue ? ` Not confirmed in 3 days${l.pending.reminderAt ? `; reminder sent ${formatDate(l.pending.reminderAt)}` : '; the reminder goes out with the daily run'}.` : ''}
            </p>
            {l.pending.vialChanged && l.pending.decidedDose && (
              <p className="mt-1 text-ink-muted" data-testid="pending-vial-changed">
                The vial changed after this was decided ({doseLine(l.pending.decidedDose)} then), so {firstName}'s notice shows the units for the vial in use.
              </p>
            )}
            {l.pending.dose && !l.pending.dose.measurable && (
              <p className="mt-1 font-semibold text-risk-orange">The vial in use cannot measure this dose. Record a vial that can, or withdraw the change.</p>
            )}
          </div>
        </div>
      )}

      {/* A new vial waiting for the patient */}
      {l.vial && (
        <div className={`mt-3 flex gap-3 rounded-lg px-3.5 py-3 ${l.vial.overdue ? 'bg-risk-orange-soft' : 'bg-info-soft'}`} data-testid="vial-change">
          <span className={`mt-0.5 shrink-0 ${l.vial.overdue ? 'text-risk-orange' : 'text-navy'}`}>
            <IconAlert size={18} />
          </span>
          <div className="min-w-0 text-[13.5px] text-navy">
            <p className="font-semibold">New vial waiting for {firstName} to confirm the units</p>
            <p className="mt-0.5 text-ink-muted">
              Recorded {formatDate(l.vial.since)}. {l.vial.dose ? `${l.current.text} mg is ${doseLine(l.vial.dose)}. ` : ''}
              {l.vial.overdue ? `Not confirmed in 3 days${l.vial.reminderAt ? `; reminder sent ${formatDate(l.vial.reminderAt)}` : '; the reminder goes out with the daily run'}.` : ''}
            </p>
          </div>
        </div>
      )}

      {/* Open check-in alerts, before the checks: a RED item asks for an urgent assessment (OPT-HE-004) */}
      {l.alerts.length > 0 && <AlertList alerts={l.alerts} patientId={patientId} canResolve={viewer.canEdit} />}

      {/* Readiness and the check-in */}
      {l.active && (
        <div className="mt-3 grid gap-3 lg:grid-cols-2 lg:items-start">
          <div className="rounded-lg border border-hairline px-3.5 py-3" data-testid="readiness-checks">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                <p className="font-display text-[16px] text-navy" style={{ fontWeight: 560 }}>Readiness checks</p>
                <InfoHint label="About the readiness checks">
                  The next step is ready for review only when all eight pass (OPT-STEP-003). Otherwise the card says Stay at
                  current dose with the reason. Checked again every day and with every check-in. Hover a reason for its rule.
                </InfoHint>
              </div>
              <span className="text-[12px] text-ink-muted">as of today</span>
            </div>
            {l.readiness ? (
              <ul className="mt-1 divide-y divide-hairline/70">
                {l.readiness.checks.map((c) => (
                  <li key={c.check} className="flex gap-2.5 py-2" title={c.rule}>
                    <span
                      className={`mt-[1px] grid h-5 w-5 shrink-0 place-items-center rounded-full ${c.pass ? 'bg-mint text-forest' : 'bg-risk-yellow-soft text-risk-yellow'}`}
                      aria-label={c.pass ? 'Passes' : 'Does not pass'}
                    >
                      {c.pass ? <IconCheck size={13} /> : <span className="block h-[2px] w-2.5 rounded bg-current" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[13.5px] text-navy">{c.label}</span>
                      {c.reasons.map((r) => (
                        <span key={`${r.rule}${r.why}`} className="mt-0.5 block text-[12.5px] leading-snug text-ink-muted" title={r.rule}>
                          {r.why}
                        </span>
                      ))}
                      {c.note && <span className="mt-0.5 block text-[12.5px] leading-snug text-ink-muted">{c.note}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-[13px] text-ink-muted">No Monthly check-in yet. The checks run with {firstName}'s first one on this plan.</p>
            )}
          </div>

          <CheckinPanel ladder={l} firstName={firstName} />
        </div>
      )}

      {/* The three buttons */}
      {l.active && (
        <div className="mt-3 border-t border-hairline pt-3">
          {l.decisionForLatest && !l.pending && (
            <p className="mb-2 text-[12.5px] text-ink-muted">
              {decisionWord(l.decisionForLatest.kind)} for this check-in on {formatDate(l.decisionForLatest.at)}
              {l.decisionForLatest.by ? ` by ${l.decisionForLatest.by}` : ''}.
            </p>
          )}
          {viewer.canDecide ? (
            <>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                {!l.pending && next.toText && (
                  <Button onClick={() => setDialog('approve')} disabled={!next.allowed} title={next.allowed ? undefined : blockerTitle(next.blockers)} className="w-full sm:w-auto">
                    {next.ceiling ? `Approve ceiling step to ${next.toText} mg` : `Approve step to ${next.toText} mg`}
                  </Button>
                )}
                <Button variant="secondary" onClick={() => setDialog('stay')} disabled={!l.options.stay.allowed} title={l.options.stay.allowed ? undefined : blockerTitle(l.options.stay.blockers)} className="w-full sm:w-auto">
                  {l.options.stay.withdraws ? `Withdraw, stay at ${l.current.text} mg` : `Stay at ${l.current.text} mg`}
                </Button>
                {down.toText && (
                  <Button variant="secondary" onClick={() => setDialog('step_down')} disabled={!down.allowed} title={down.allowed ? undefined : blockerTitle(down.blockers)} className="w-full sm:w-auto">
                    Step down to {down.toText} mg
                  </Button>
                )}
              </div>
              {down.suggested && down.allowed && !l.decisionForLatest && (
                <p className="mt-2 text-[12.5px] text-navy" data-testid="step-down-suggested" title={down.suggestedBy.map((s) => s.rule).join(', ')}>
                  <span className="font-semibold">Step down offered:</span> {down.suggestedBy.map((s) => s.why).join('; ')}.
                </p>
              )}
              {!next.allowed && !l.pending && unmeasurable && (
                <p className="mt-2 text-[12.5px] text-risk-orange" title={unmeasurable.rule ?? undefined}>{unmeasurable.text}</p>
              )}
              {!down.toText && down.suggestedBy.length > 0 && !l.decisionForLatest && (
                <p className="mt-2 text-[12.5px] text-ink-muted" title={down.suggestedBy.map((s) => s.rule).join(', ')}>
                  {down.suggestedBy.map((s) => s.why).join('; ')}. {down.blockers[0]?.text}
                </p>
              )}
            </>
          ) : (
            <p className="text-[13px] text-ink-muted" data-testid="decide-blocked">
              {viewer.canEdit ? `${viewer.decideBlocker ?? 'Only a prescriber decides a dose step.'} You can see everything here.` : 'A prescriber approves, stays or steps down a dose. You can see everything here.'}
            </p>
          )}
        </div>
      )}

      {l.history.length > 0 && <History ladder={l} />}

      {dialog && (
        <MicrodoseDecisionDialog
          kind={dialog}
          ladder={l}
          viewer={viewer}
          firstName={firstName}
          onClose={() => setDialog(null)}
          patientId={patientId}
        />
      )}
    </Section>
  );
}

function decisionWord(kind: string): string {
  return kind === 'approved' ? 'Step approved' : kind === 'step_down' ? 'Stepped down' : 'Stay chosen';
}

/* ------------------------------------------------------------------ *
 * The check-in against the baseline
 * ------------------------------------------------------------------ */

function CheckinPanel({ ladder: l, firstName }: { ladder: SignedLadder; firstName: string }) {
  const c = l.checkin;
  if (!c) {
    return (
      <div className="rounded-lg border border-hairline px-3.5 py-3">
        <p className="font-display text-[16px] text-navy" style={{ fontWeight: 560 }}>Latest check-in</p>
        <p className="mt-2 text-[13px] text-ink-muted">{firstName} has not answered a Monthly check-in on this plan yet.</p>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-hairline px-3.5 py-3" data-testid="checkin-panel">
      <div className="flex items-center justify-between gap-2">
        <p className="font-display text-[16px] text-navy" style={{ fontWeight: 560 }}>
          Check-in {formatDate(c.submittedAt)}
          {c.month ? <span className="ml-1.5 font-sans text-[12.5px] font-normal text-ink-muted">month {c.month}</span> : null}
        </p>
        <span className="text-[12px] text-ink-muted">{c.ageDays === 0 ? 'today' : `${c.ageDays} ${c.ageDays === 1 ? 'day' : 'days'} ago`}</span>
      </div>

      {c.scores && (
        <table className="mt-2 w-full text-[13px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.05em] text-ink-muted">
              <th className="pb-1 font-semibold">0 to 10</th>
              <th className="pb-1 pl-3 text-right font-semibold">Now</th>
              <th className="pb-1 pl-3 text-right font-semibold">Baseline</th>
              <th className="pb-1 pl-3 text-right font-semibold">Change</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline/60">
            {c.scores.map((s) => (
              <tr key={s.key}>
                <td className="py-1.5 pr-2 text-navy">{s.label}</td>
                <td className="py-1.5 pl-3 text-right text-navy">{s.value ?? <span className="text-ink-muted">Not asked</span>}</td>
                <td className="py-1.5 pl-3 text-right text-ink-muted">{s.baseline ?? 'None'}</td>
                <td className={`py-1.5 pl-3 text-right ${s.change === null ? 'text-ink-muted' : s.change <= -3 ? 'font-semibold text-risk-orange' : 'text-navy'}`}>
                  {s.change === null ? '' : s.change > 0 ? `+${s.change}` : s.change === 0 ? '0' : `${s.change}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="mt-3 border-t border-hairline pt-2">
        <Eyebrow>Side effects and dosing</Eyebrow>
        <dl className="mt-1.5 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 text-[13px]">
          {[...c.sideEffects, ...c.dosing].map((row) => (
            <div key={row.ref} className="contents">
              <dt className="text-ink-muted">{row.label}</dt>
              <dd className={`text-right ${isNotable(row.ref, row.answer) ? 'font-semibold text-risk-orange' : 'text-navy'}`}>
                {row.answer ?? <span className="text-ink-muted">Not asked</span>}
              </dd>
            </div>
          ))}
        </dl>
        {c.detail && (
          <p className="mt-2 rounded-md bg-paper-deep px-2.5 py-1.5 text-[12.5px] text-navy">
            <span className="text-ink-muted">In {firstName}'s words: </span>
            {c.detail}
          </p>
        )}
      </div>
    </div>
  );
}

/** Answers worth the eye: anything above mild, a missed run of doses, a stay request. Display only; the rules are on the server. */
function isNotable(ref: string, answer: string | null): boolean {
  if (!answer) return false;
  if (ref === 'opt_stay_pref') return answer === 'Yes';
  if (ref === 'glp1_missed_in_row') return answer === 'I missed 2 or more in a row';
  if (ref === 'opt_benefit' || ref === 'opt_overall_change') return answer === 'Much worse';
  if (ref === 'retain_other_side_effects') return answer !== 'Not at all';
  return !['None', 'Mild', 'No', 'Once', 'Sometimes'].includes(answer);
}

/* ------------------------------------------------------------------ *
 * Alerts
 * ------------------------------------------------------------------ */

function AlertList({ alerts, patientId, canResolve }: { alerts: SignedLadder['alerts']; patientId: string; canResolve: boolean }) {
  const client = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const [text, setText] = useState('');
  const resolve = useMutation({
    mutationFn: (id: string) => api.post(`/microdose/alerts/${id}/resolve`, { resolution: text }),
    onSuccess: () => {
      setOpen(null);
      setText('');
      void client.invalidateQueries({ queryKey: ['microdose', patientId] });
      void client.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
  return (
    <div className="mt-3 rounded-lg border border-hairline px-3.5 py-3" data-testid="microdose-alerts">
      <div className="flex items-center gap-1.5">
        <p className="font-display text-[16px] text-navy" style={{ fontWeight: 560 }}>Open check-in alerts</p>
        <InfoHint label="About check-in alerts">
          Raised by the Monthly check-in rules of the microdose workbook (tabs 08 and 10). RED is severe or persistent
          abdominal pain, recurrent vomiting, or not being able to keep fluids down: hold the medication pathway, urgent
          clinician assessment, a clinician alert at once (OPT-HE-004). Each one is also in Physician Alerts until you
          resolve it here. Hover an alert for its rule.
        </InfoHint>
      </div>
      <ul className="mt-1 divide-y divide-hairline/70">
        {alerts.map((a) => (
          <li key={a.id} className="py-2">
            <div className="flex flex-wrap items-start gap-x-2.5 gap-y-1.5" title={a.ruleId}>
              <span className={`mt-[1px] shrink-0 rounded-[5px] px-1.5 py-[3px] text-[10.5px] font-semibold uppercase leading-none tracking-[0.04em] ${LEVEL_CLASS[a.level]}`}>
                {a.level}
              </span>
              <span className="min-w-0 flex-1 text-[13.5px] text-navy">
                {plainReason(a.text)}
                <span className="ml-1.5 whitespace-nowrap text-[11.5px] text-ink-muted">{formatDate(a.createdAt)}</span>
              </span>
              {canResolve && open !== a.id && (
                <button type="button" className="shrink-0 text-[12.5px] font-semibold text-forest hover:underline" onClick={() => { setOpen(a.id); setText(''); resolve.reset(); }}>
                  Resolve
                </button>
              )}
            </div>
            {open === a.id && (
              <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-start">
                <input
                  className={`${inputClass} !py-2`}
                  placeholder="What you did, for the decision log"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  aria-label="Resolution"
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => resolve.mutate(a.id)} loading={resolve.isPending} disabled={text.trim().length < 5}>
                    Resolve
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setOpen(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            {open === a.id && resolve.error && (
              <p className="mt-1 text-[12.5px] text-alert" role="alert">{resolve.error instanceof Error ? resolve.error.message : 'Could not resolve'}</p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The dispensed vial (OPT-STEP-006, OPT-STEP-002)
 * ------------------------------------------------------------------ */

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function VialForm({ ladder: l, patientId, firstName, onClose }: { ladder: SignedLadder; patientId: string; firstName: string; onClose: () => void }) {
  const client = useQueryClient();
  const [concentration, setConcentration] = useState(l.dispense ? String(l.dispense.concentrationMgPerMl) : '');
  const [date, setDate] = useState(todayIso());
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [reference, setReference] = useState('');
  const value = Number(concentration);
  const valid = concentration.trim() !== '' && Number.isFinite(value) && value > 0;
  // A fill dated before the vial in use is history only; the checks apply to the vial that becomes the one in use.
  const becomesCurrent = !l.dispense || date >= l.dispense.dispensedAt.slice(0, 10);
  const changed = valid && becomesCurrent && l.dispense !== null && Math.abs(l.dispense.concentrationMgPerMl - value) > 1e-9;

  /** OPT-STEP-006: every dose the patient may be on now must be measurable at the new vial. */
  const inUse = useMemo(() => {
    if (!valid || !becomesCurrent) return [];
    const doses = [{ label: 'current dose', mg: l.current.mg, text: l.current.text }];
    if (l.pending) doses.push({ label: 'change waiting', mg: l.pending.toMg, text: l.pending.toText });
    return doses.map((d) => ({ ...d, ...unitsAt(d.mg, value, l.measurability) }));
  }, [valid, becomesCurrent, value, l]);
  const blocked = inUse.filter((d) => !d.measurable);

  const save = useMutation({
    mutationFn: () =>
      api.post(`/microdose/ladders/${l.id}/dispenses`, {
        concentrationMgPerMl: value,
        dispensedAt: date,
        note: note.trim() || null,
        confirmConcentrationChange: changed ? confirmed : false,
        prescriptionReference: changed ? reference.trim() || null : null,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['microdose', patientId] });
      onClose();
    },
  });
  const ready = valid && Boolean(date) && blocked.length === 0 && (!changed || confirmed);

  return (
    <div className="mt-3 rounded-lg border border-forest/30 bg-mint/40 px-3.5 py-3" data-testid="vial-form">
      <div className="flex items-center gap-1.5">
        <p className="font-display text-[16px] text-navy" style={{ fontWeight: 560 }}>Record the dispensed vial</p>
        <InfoHint label="About the vial">
          The concentration printed on the vial the pharmacy filled. mL and U-100 units for every dose are worked out from it,
          and a dose under {l.measurability.minUnits} units or off a half-unit mark cannot be given at it (OPT-STEP-006). A new
          concentration needs a new prescription (OPT-STEP-002), and the patient confirms the new units before using it.
        </InfoHint>
      </div>
      <div className="mt-2 grid gap-2.5 sm:grid-cols-[160px_170px_minmax(0,1fr)]">
        <label className="block">
          <span className="mb-1 block text-[12px] font-semibold text-navy">Concentration (mg/mL)</span>
          <input className={`${inputClass} !py-2`} inputMode="decimal" value={concentration} onChange={(e) => { setConcentration(e.target.value.replace(/[^\d.]/g, '')); setConfirmed(false); }} placeholder="5" data-testid="vial-concentration" />
        </label>
        <label className="block">
          <span className="mb-1 block text-[12px] font-semibold text-navy">Fill date</span>
          <input type="date" className={`${inputClass} !py-2`} value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label className="block">
          <span className="mb-1 block text-[12px] font-semibold text-navy">Note (optional)</span>
          <input className={`${inputClass} !py-2`} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="Pharmacy, lot or anything worth keeping" />
        </label>
      </div>
      {valid && (
        <ul className="mt-2.5 grid grid-cols-2 gap-1.5 sm:grid-cols-4" aria-label="Units at this concentration">
          {l.steps.map((s) => {
            const u = unitsAt(s.mg, value, l.measurability);
            return (
              <li key={s.step} className={`rounded-md border px-2 py-1.5 text-[12.5px] ${u.measurable ? 'border-hairline bg-white text-navy' : 'border-risk-orange/40 bg-risk-orange-soft text-risk-orange'}`}>
                <span className="font-semibold">{s.text} mg</span> · {unitCount(u.units)}
                <span className="block text-[11.5px] opacity-80">{u.measurable ? `${plain(u.mL)} mL` : 'Cannot be measured'}</span>
              </li>
            );
          })}
        </ul>
      )}

      {blocked.length > 0 && (
        <p className="mt-2.5 rounded-md bg-risk-orange-soft px-3 py-2 text-[13px] font-semibold text-risk-orange" role="alert" data-testid="vial-unmeasurable">
          {blocked.map((d) => `${d.text} mg (${d.label}) is ${unitCount(d.units)} at ${plain(value)} mg/mL, which a U-100 syringe cannot measure.`).join(' ')} Choose another concentration.
        </p>
      )}

      {changed && blocked.length === 0 && (
        <div className="mt-2.5 rounded-md border border-risk-yellow/40 bg-risk-yellow-soft px-3 py-2.5 text-[13px] text-navy" data-testid="vial-concentration-change">
          <p className="font-semibold">
            The concentration changes from {plain(l.dispense!.concentrationMgPerMl)} to {plain(value)} mg/mL.
          </p>
          <p className="mt-0.5">
            {firstName} draws {inUse.map((d) => `${unitCount(d.units)} for ${d.text} mg`).join(' and ')} from this vial, and confirms the new units in the portal before using it.
            {l.pending ? ` The change waiting for ${firstName} shows the new units too.` : ''}
          </p>
          <label className="mt-2 flex cursor-pointer items-start gap-2.5">
            <input type="checkbox" className="mt-0.5 h-[17px] w-[17px] shrink-0 accent-[var(--forest)]" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="vial-prescription-confirm" />
            <span>A new prescription for {plain(value)} mg/mL covers this vial</span>
          </label>
          <label className="mt-2 block">
            <span className="mb-1 block text-[12px] font-semibold">Prescription reference (optional)</span>
            <input className={`${inputClass} !py-2`} value={reference} maxLength={120} onChange={(e) => setReference(e.target.value)} placeholder="Rx number or pharmacy order" />
          </label>
        </div>
      )}

      {save.error && <p className="mt-2 text-[12.5px] text-alert" role="alert">{save.error instanceof Error ? save.error.message : 'Could not save'}</p>}
      <div className="mt-3 flex gap-2">
        <Button size="sm" onClick={() => save.mutate()} disabled={!ready} loading={save.isPending}>
          Save vial
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * History (OPT-STEP-010)
 * ------------------------------------------------------------------ */

function History({ ladder: l }: { ladder: SignedLadder }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 border-t border-hairline pt-2">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="flex w-full items-center justify-between gap-2 py-1 text-left">
        <span className="text-[13.5px] font-semibold text-navy">Dose history ({l.history.length})</span>
        <span className={`text-ink-muted transition-transform ${open ? 'rotate-180' : ''}`}>
          <IconChevronDown size={16} />
        </span>
      </button>
      {open && (
        <ol className="mt-1 divide-y divide-hairline/70" data-testid="dose-history">
          {l.history.map((h) => (
            <li key={h.id} className="flex flex-col gap-0.5 py-2 text-[13px] sm:flex-row sm:gap-3" title={h.ruleIds.join(', ') || undefined}>
              <span className="w-[110px] shrink-0 text-ink-muted">{formatDate(h.at)}</span>
              <span className="min-w-0 text-navy">
                {historyLine(h)}
                {h.reason && <span className="block text-[12.5px] text-ink-muted">{plainReason(h.reason)}</span>}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function historyLine(h: SignedLadder['history'][number]): string {
  const dose = h.units !== null && h.concentrationMgPerMl !== null ? ` (${plain(h.units)} units at ${plain(h.concentrationMgPerMl)} mg/mL)` : '';
  const signed = h.signatureMethod ? `, signed with ${h.signatureMethod === 'pin' ? 'PIN' : 'passkey'}` : '';
  switch (h.kind) {
    case 'approved':
      return `Step approved, ${h.fromText} to ${h.toText} mg${dose}, by ${h.by ?? 'a clinician'}${signed}`;
    case 'step_down':
      return `Stepped down, ${h.fromText} to ${h.toText} mg${dose}, by ${h.by ?? 'a clinician'}${signed}`;
    case 'stay':
      return `Stay at ${h.fromText} mg, by ${h.by ?? 'a clinician'}`;
    case 'vial_change':
      return `New vial, ${h.concentrationMgPerMl !== null ? plain(h.concentrationMgPerMl) : ''} mg/mL: ${h.toText} mg is ${h.units !== null ? plain(h.units) : ''} units, recorded by ${h.by ?? 'a clinician'}`;
    case 'acknowledged':
      return h.refKind === 'vial_change'
        ? `Patient confirmed the units for the new vial${dose}`
        : `Patient confirmed ${h.toText} mg${dose}${h.effectiveFrom ? `, on the schedule from ${formatDate(h.effectiveFrom)}` : ''}`;
    case 'reminder':
      return h.refKind === 'vial_change' ? 'Reminder: the new vial not confirmed in 3 days' : `Reminder: ${h.toText} mg not confirmed in 3 days`;
    default:
      return h.kind;
  }
}
